/**
 * SubscriptionUpsellNote — the conversion surface on a locked payment method.
 *
 * v2 port of legacy's `SubscriptionInstructionUpsell` as rendered by
 * `BillingMethodInstructionCard`: when invoice-by-email is not permitted, the
 * card explains why and links to the pricing page. Rewritten here rather than
 * imported so the billing step keeps no dependency on `pages/PurchaseFlow/`,
 * which is due to be deleted with the legacy flow.
 *
 * Copy and the `unblock_invoicing` funnel event are legacy's own, so the
 * existing translations and dashboards keep working.
 *
 * **Withheld inside the Propstack embed.** Its CTA calls
 * `externalNavigate` to the public pricing site, which inside the iframe ejects
 * the broker out of the CRM onto our marketing page — and the journey it starts
 * cannot succeed for an embed broker anyway (they have no Backbone password).
 * Legacy drops the same CTA and keeps the "Subscribers only" badge, which is
 * rendered by the caller and is therefore unaffected.
 *
 * The gate lives HERE rather than on the caller's `!allowed` so it cannot be
 * forgotten by the next surface that wants this note: the component that leaves
 * the iframe is the one that refuses to.
 */

import React from 'react'
import { Stack } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'

import { MUIButton } from 'components/common/MUIButton'
import { SystemMessage } from 'components/common/SystemMessage'
import { useUserData } from 'components/contexts/UserDataContext'
import { logAnalyticsEvent } from 'utils/analytics'
import { externalNavigate } from 'utils/helpers'
import { getPricingUrl } from 'utils/localization'
import i18n from 'translations/i18n'

import { usePfEmbed } from '../../../routing/usePfEmbed'

/**
 * @example
 * <SubscriptionUpsellNote />
 */
export const SubscriptionUpsellNote: React.FC = () => {
  const { t } = useTranslation(['card_new_user_invoice_paymethod', 'instruction_upsell'])
  const { clientData } = useUserData()
  const { isEmbed } = usePfEmbed()

  if (isEmbed) return null

  return (
    <SystemMessage
      variant="system"
      title={t('card_new_user_invoice_paymethod:title')}
      isRawContent
      sx={{ mt: 0.8 }}
      message={
        <Stack gap={0.8} alignItems="flex-start">

          {/* The copy carries its own <strong> and <br /> markup, so it stays one flex item. */}
          <span>
            <Trans t={t} i18nKey="card_new_user_invoice_paymethod:description" />
          </span>

          <MUIButton
            type="secondaryBorder"
            size="sm"
            onClick={() => {
              logAnalyticsEvent('unblock_invoicing', {
                userEmail: clientData?.email,
                organizationId: clientData?.organizationId,
              })
              externalNavigate(getPricingUrl(i18n.language))
            }}
          >
            {t('instruction_upsell:link_cta')}
          </MUIButton>

        </Stack>
      }
    />
  )
}
